import { Loader } from "./Loader";
import { LoaderEvent } from "../events/LoaderEvent";
import { BaseLoader } from "./BaseLoader";
import { Event } from "../events/Event";
import { ScriptLoader } from "./ScriptLoader";

export class LoadingQueue extends Loader {
    private _state: number;
    private _asyncScriptExecution: boolean;
    private _loaders: any;
    private _scriptLoaders: any;
    private _loadersCount: number = 0;
    private _completeLoadersCount: number = 0;
    private _progress: number = 0;

    constructor(asyncScriptExecution?: boolean, id?: string) {
        super();

        this._id = id;

        if (this._id != undefined)
            Loader.addLoader(this);

        this._state = BaseLoader.STATE_IDLE;
        this._asyncScriptExecution = asyncScriptExecution;
        this._loaders = [];
        this._scriptLoaders = [];
    }

    isIdle() {
        return this._state == BaseLoader.STATE_IDLE;
    }

    isLoading() {
        return this._state == BaseLoader.STATE_LOADING;
    }

    isLoaded() {
        return this._state == BaseLoader.STATE_LOADED;
    }

    hasError() {
        return this._state == BaseLoader.STATE_ERROR;
    }

    addLoader(loader: BaseLoader) {
        var loadersArray = loader instanceof ScriptLoader ? this._scriptLoaders : this._loaders;

        const loaderIndex = loadersArray.indexOf(loader);
        if (loaderIndex != -1)
            return;

        loadersArray.push(loader);

        loader.addEventListener(LoaderEvent.COMPLETE, this.onLoaderLoaded, this);
        loader.addEventListener(LoaderEvent.PROGRESS, this.onLoaderProgress, this);
        loader.addEventListener(LoaderEvent.ERROR, this.onLoaderError, this);

        this._loadersCount++;

        // TODO: what happens in the other states of the queue and loader

        if (this.isLoading()) {
            loader.load();
        }
    }

    removeLoader(loader: BaseLoader) {
        var loadersArray = loader instanceof ScriptLoader ? this._scriptLoaders : this._loaders;

        const loaderIndex = loadersArray.indexOf(loader);
        if (loaderIndex == -1)
            return;

        loadersArray.splice(loaderIndex, 1);

        this.removeLoaderListeners(loader);

        this._loadersCount--;

        // TODO: the state of the removed loader should be checked and the state of
        // the queue updated accordingly
    }

    load() {
        if (this._state == BaseLoader.STATE_LOADING)
            return;
        else if (this._state == BaseLoader.STATE_LOADED) {
            var self = this;
            // call loaderHasLoaded asynchronously
            setTimeout(function () {
                // the number of complete loaders should be decreased, because "loaderHasLoaded" increases this count and the comparison
                // (total == loaded) will fail
                self._completeLoadersCount--;
                self.loaderHasLoaded();
            }, 0);
            return;
        }

        this._completeLoadersCount = 0;
        this._progress = 0;

        this._state = BaseLoader.STATE_LOADING;

        for (var loaderIndex in this._loaders)
            this._loaders[loaderIndex].load();

        if (this._scriptLoaders.length != 0) {
            if (this._asyncScriptExecution) {
                for (var loaderIndex in this._scriptLoaders)
                    this._scriptLoaders[loaderIndex].load();
            }
            else {
                this._scriptLoaders[0].load();
            }
        }
    }

    stop() {
        if (this._state != BaseLoader.STATE_LOADING)
            return;

        this._state = BaseLoader.STATE_IDLE;

        let length = this._loaders.length;
        for (let i = 0; i < length; i++)
            this._loaders[i].stop();

        length = this._scriptLoaders.length;
        for (let i = 0; i < length; i++)
            this._scriptLoaders[i].stop();
    }

    getData(): any {
        return null;
    }

    dispose() {
        if (this._id != undefined)
            Loader.removeLoader(this);

        let loaderIndex;
        let length = this._loaders.length;
        for (loaderIndex = 0; loaderIndex < length; loaderIndex++) {
            this.disposeLoader(this._loaders[loaderIndex]);
        }

        length = this._scriptLoaders.length;
        for (loaderIndex = 0; loaderIndex < length; loaderIndex++) {
            this.disposeLoader(this._scriptLoaders[loaderIndex]);
        }

        this._loaders.length = 0;
        this._scriptLoaders.length = 0;

        super.dispose();
    }

    private disposeLoader(loader: BaseLoader) {
        this.removeLoaderListeners(loader);

        loader.dispose();
    }

    private removeLoaderListeners(loader: BaseLoader) {
        loader.removeEventListener(LoaderEvent.COMPLETE, this.onLoaderLoaded, this);
        loader.removeEventListener(LoaderEvent.PROGRESS, this.onLoaderProgress, this);
        loader.removeEventListener(LoaderEvent.ERROR, this.onLoaderError, this);
    }

    private loaderHasLoaded(event?: Event) {
        if (event) {
            this.dispatchEvent(new LoaderEvent(LoaderEvent.CHILD_COMPLETE, event.data),
                event.target);
        }

        this._completeLoadersCount++;

        if (this._completeLoadersCount == this._loadersCount) {
            this._state = BaseLoader.STATE_LOADED;

            // all loaders have completed
            this._progress = 1;
            this.dispatchEvent(new LoaderEvent(LoaderEvent.PROGRESS, {progress: 1}));
            this.dispatchEvent(new LoaderEvent(LoaderEvent.COMPLETE));
        }
    }

    private onLoaderLoaded(event: Event) {
        if (this._state == BaseLoader.STATE_ERROR || this._state == BaseLoader.STATE_IDLE)
            return;

        if (event.target instanceof ScriptLoader && !this._asyncScriptExecution) {
            var loaderIndex: number = this._scriptLoaders.indexOf(event.target);
            if (loaderIndex < this._scriptLoaders.length - 1) {
                this._scriptLoaders[loaderIndex + 1].load();
            }
        }

        this.loaderHasLoaded(event);
    }

    private onLoaderProgress(event: Event) {
        this._progress += event.data.progressChange / this._loadersCount;
        this._progress = Math.min(this._progress, 1);

        if (this._state == BaseLoader.STATE_ERROR || this._state == BaseLoader.STATE_IDLE)
            return;

        var data =
            {
                progress: this._progress
            };

        this.dispatchEvent(new LoaderEvent(LoaderEvent.CHILD_PROGRESS, event.data),
            event.target);
        this.dispatchEvent(new LoaderEvent(LoaderEvent.PROGRESS, data));
    }

    private onLoaderError(event: Event) {
        this.stop();
        this._state = BaseLoader.STATE_ERROR;

        this.dispatchEvent(new LoaderEvent(LoaderEvent.CHILD_ERROR, event.data), event.target);
        this.dispatchEvent(new LoaderEvent(LoaderEvent.ERROR));
    }
}
